import { useState } from 'react';
import './AuthTypography.css';
import './RegistrationPage.css';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';

export interface FullPatientRegistration {
  // the FullUser fields:
  id: string;
  customerId: String;
  userName: String;
  firstName: String;
  familyName: String;
  email: String;
  mobile: String;
  password: String;
  userDetailId: String;
  extId: String;
  salutation: String;
  title: String;
  zipCode: String;
  town: String;
  phones: String[];
  addressLines: String[];
  languages: String[];
  dateOfBirth: String;
  comment: String;
  // the fields from Patient:
  officeId: String;
  patientType: String;
  //patientInfo: JSON; // not yet
}

type RegistrationFormData = {
  firstName: string;
  familyName: string;
  dateOfBirth: string;
  address: string;
  zipCode: string;
  town: string;
  email: string;
  mobile: string;
  password: string;
  repeatedPassword: string;
};

const initialFormData: RegistrationFormData = {
  firstName: '',
  familyName: '',
  dateOfBirth: '',
  address: '',
  zipCode: '',
  town: '',
  email: '',
  mobile: '',
  password: '',
  repeatedPassword: '',
};

const buildUserName = (formData: RegistrationFormData) =>
  formData.email.trim() ||
  `${formData.firstName.trim()}.${formData.familyName.trim()}`
    .toLowerCase()
    .replace(/\s+/g, '');

const buildRegistrationPayload = (formData: RegistrationFormData): FullPatientRegistration => ({
  id: '',
  customerId: '',
  userName: buildUserName(formData),
  firstName: formData.firstName.trim(),
  familyName: formData.familyName.trim(),
  email: formData.email.trim(),
  mobile: formData.mobile.trim(),
  password: formData.password,
  userDetailId: '',
  extId: '',
  salutation: '',
  title: '',
  zipCode: formData.zipCode.trim(),
  town: formData.town.trim(),
  phones: formData.mobile.trim() ? [formData.mobile.trim()] : [],
  addressLines: formData.address.trim() ? [formData.address.trim()] : [],
  languages: [],
  dateOfBirth: formData.dateOfBirth.trim(),
  comment: '',
  officeId: '',
  patientType: '',
});

export function ObsoleteRegistrationPage() {
  const [formData, setFormData] = useState<RegistrationFormData>(initialFormData);
  const [acceptedDatenschutzerklaerung, setAcceptedDatenschutzerklaerung] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onChange = (field: keyof RegistrationFormData, value: string) => {
    setFormData((currentValue) => ({
      ...currentValue,
      [field]: value,
    }));
  };

  const requiredFieldsAreFilled =
    formData.firstName.trim() !== '' &&
    formData.familyName.trim() !== '' &&
    formData.dateOfBirth.trim() !== '' &&
    formData.address.trim() !== '' &&
    formData.zipCode.trim() !== '' &&
    formData.town.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.mobile.trim() !== '' &&
    formData.password.trim() !== '' &&
    formData.repeatedPassword.trim() !== '';

  const passwordsMatch = formData.password === formData.repeatedPassword;
  const canSubmit =
    requiredFieldsAreFilled &&
    passwordsMatch &&
    acceptedDatenschutzerklaerung &&
    acceptedTerms;

  const handleSubmit = async (event: React.FormEvent) => {

    console.log("handleSubmit:");

    event.preventDefault();

    if (!canSubmit || loading) {
      return;
    }

    if (!passwordsMatch) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setError('');
    setLoading(true);

    console.log("handleSubmit: " + JSON.stringify(buildRegistrationPayload(formData)));

    try {
      const response = await fetch(`${API_BASE_URL}/patients/register-full`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(buildRegistrationPayload(formData)),
      });

      if (response.ok) {
        navigate('/login');
        return;
      }

      if (response.status === 409) {
        setError('Benutzer ist bereits registriert.');
        return;
      }

      setError(`Registrierung fehlgeschlagen: ${response.status} ${response.statusText}`);
    } catch (err) {
      setError(`Registrierung fehlgeschlagen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page registration-page min-h-screen w-screen bg-[#fbf7f1] px-5 py-8 text-slate-800 md:px-9 md:py-12">
      <form onSubmit={handleSubmit} className="mx-auto max-w-[1420px]">
        <section className="rounded-[28px] border border-[#efe7da] bg-white px-6 py-9 shadow-[0_18px_40px_rgba(19,19,19,0.04)] md:px-14 md:py-14">
          <div>
            <h1 className="text-3xl font-semibold leading-tight text-slate-900 md:text-[34px]">
              Patientenregistrierung
            </h1>
            <p className="mt-4 text-lg text-slate-400 md:text-xl">
              Bitte füllen Sie das Formular aus, um sich für das Patientenportal zu registrieren.
            </p>
          </div>

          <div className="mt-20 grid gap-x-8 gap-y-9 md:grid-cols-2 md:gap-y-10">
            <label className="block">
              <span className="text-base font-semibold text-slate-400">Vorname</span>
              <input
                type="text"
                value={formData.firstName}
                onChange={(event) => onChange('firstName', event.target.value)}
                required
                className="mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>

            <label className="block">
              <span className="text-base font-semibold text-slate-400">Name</span>
              <input
                type="text"
                value={formData.familyName}
                onChange={(event) => onChange('familyName', event.target.value)}
                required
                className="mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>

            <label className="block md:col-span-2">
              <span className="text-base font-semibold text-slate-400">Geburtsdatum</span>
              <input
                type="text"
                value={formData.dateOfBirth}
                onChange={(event) => onChange('dateOfBirth', event.target.value)}
                required
                className="auth-numeric mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>

            <label className="block md:col-span-2">
              <span className="text-base font-semibold text-slate-400">Strasse und Hausnummer</span>
              <input
                type="text"
                value={formData.address}
                onChange={(event) => onChange('address', event.target.value)}
                required
                className="mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>

            <label className="block">
              <span className="text-base font-semibold text-slate-400">Postleitzahl</span>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(event) => onChange('zipCode', event.target.value)}
                required
                className="auth-numeric mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>

            <label className="block">
              <span className="text-base font-semibold text-slate-400">Ort</span>
              <input
                type="text"
                value={formData.town}
                onChange={(event) => onChange('town', event.target.value)}
                required
                className="mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>

            <label className="block md:col-span-2">
              <span className="text-base font-semibold text-slate-400">E-Mail Adresse</span>
              <input
                type="email"
                value={formData.email}
                onChange={(event) => onChange('email', event.target.value)}
                required
                className="mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>

            <label className="block md:col-span-2">
              <span className="text-base font-semibold text-slate-400">Handynummer</span>
              <input
                type="tel"
                value={formData.mobile}
                onChange={(event) => onChange('mobile', event.target.value)}
                required
                className="mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>

            <label className="block">
              <span className="text-base font-semibold text-slate-400">Neues Passwort</span>
              <input
                type="password"
                value={formData.password}
                onChange={(event) => onChange('password', event.target.value)}
                required
                className="mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>

            <label className="block">
              <span className="text-base font-semibold text-slate-400">Passwort wiederholen</span>
              <input
                type="password"
                value={formData.repeatedPassword}
                onChange={(event) => onChange('repeatedPassword', event.target.value)}
                required
                className="mt-3 h-[76px] w-full rounded-2xl border border-[#efe7da] bg-white px-6 text-lg text-slate-800 outline-none transition focus:border-[#c4a67b] focus:ring-1 focus:ring-[#c4a67b]"
              />
            </label>
          </div>

          <div className="mt-12 space-y-6">
            <label className="flex items-start gap-5 text-lg leading-6 text-slate-400">
              <input
                type="checkbox"
                checked={acceptedDatenschutzerklaerung}
                onChange={() => setAcceptedDatenschutzerklaerung((currentValue) => !currentValue)}
                className="mt-1 h-8 w-8 shrink-0 rounded border border-[#d8ccb9] text-[#c4a67b] focus:ring-[#c4a67b]"
              />
              <span>
                Ich habe die <span className="font-semibold text-[#c4a67b]">Datenschutzerklärung</span> gelesen und
                akzeptiere diese. Ich willige ein, dass meine Daten zum Zweck der Terminvereinbarung verarbeitet werden.
              </span>
            </label>

            <label className="flex items-start gap-5 text-lg leading-6 text-slate-400">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={() => setAcceptedTerms((currentValue) => !currentValue)}
                className="mt-1 h-8 w-8 shrink-0 rounded border border-[#d8ccb9] text-[#c4a67b] focus:ring-[#c4a67b]"
              />
              <span>
                Mit Ihrer Registrierung nehmen Sie zur Kenntnis, dass nicht wahrgenommene Termine oder Termine, die
                nicht mind. 24 Stunden im Voraus abgesagt werden, in Rechnung gestellt werden können. Ergibt die
                Bonitätsprüfung ein negatives Resultat, kann die Zahlung vor Ort verlangt werden.
              </span>
            </label>
          </div>

          {error && (
            <div className="mt-8 rounded-2xl bg-red-50 px-5 py-4 text-sm font-medium text-red-600">
              {error}
            </div>
          )}
        </section>

        <div className="mt-11 flex justify-end">
          <button
            type="submit"
            disabled={!canSubmit || loading}
            className={`inline-flex min-h-[76px] w-full items-center justify-center gap-5 rounded-2xl px-8 text-xl font-semibold text-white sm:w-[530px] ${
              canSubmit && !loading
                ? 'bg-[#cbb58a] shadow-[0_10px_20px_rgba(203,181,138,0.3)] hover:bg-[#b79e73]'
                : 'cursor-not-allowed bg-[#d7c39b] opacity-80 shadow-none'
            }`}
          >
            <span>{loading ? 'Registrierung läuft...' : 'Registrierung abschliessen'}</span>
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </form>
    </main>
  );
}
